import { useMemo } from 'react';

import { Granularity, Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { granularityOptions } from '../constants/granularity';
import { EnrichedWorkspace, FormattedUsageDataResult, UsageWithBreakdown, User } from '../types';
import { formatUsageDataForChart } from '../utils/dataTransformers';

/**
 * Custom hook to format raw usage data for chart visualization
 *
 * Transforms the raw API usage data into a format suitable for the chart component,
 * enriching group IDs with human-readable names.
 *
 * @param usageData Raw usage data from the API
 * @param granularity The granularity for date formatting
 * @param groupBy The grouping type
 * @param workspacesMap Map of workspace UUIDs to workspace objects
 * @param usersMap Map of user UUIDs to user objects
 * @returns Object containing formatted data points and unique groups
 */
export function useFormattedUsageData(
  usageData: UsageWithBreakdown[] | undefined,
  granularity: Granularity,
  groupBy: Grouping,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>
): FormattedUsageDataResult {
  const formattedResult = useMemo(() => {
    if (!usageData || usageData.length === 0) {
      return { formattedData: [], groups: [] };
    }

    const currentGranularity = granularityOptions.find((option) => option.key === granularity);
    if (!currentGranularity) {
      console.error('[useFormattedUsageData] Invalid granularity:', granularity);
      return { formattedData: [], groups: [] };
    }

    // Fixed: Wait for maps to be populated before formatting to prevent showing all "Unknown" values
    // This check is only relevant for WORKSPACE and USER groupings
    if (
      (groupBy === Grouping.WORKSPACE || groupBy === Grouping.USER) &&
      (workspacesMap.size === 0 || usersMap.size === 0)
    ) {
      return { formattedData: [], groups: [] };
    }

    const result = formatUsageDataForChart(usageData, groupBy, workspacesMap, usersMap, currentGranularity.formatDate);

    return result;
  }, [usageData, granularity, groupBy, workspacesMap, usersMap]);

  return {
    formattedUsageData: formattedResult.formattedData,
    groups: formattedResult.groups,
  };
}
